import React, { useMemo } from "react";
import { Activity } from "lucide-react";
import { useGetMyLeavesQuery } from "../../store/apis/leave.api";
import { useGetMyTimesheetsQuery } from "../../store/apis/timesheet.api";
import { useGetMyRegularizationRequestsQuery } from "../../store/apis/attendanceRegularization.api";
import { CARD_CLASS } from "../../constants/dashboard";
import { getWorkflowStatusIcon } from "../../utils/badgeVariants";
import { ShimmerBlock } from "../common/Skeleton";

interface ActivityItem {
  id: string;
  timestamp: string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  tint: string;
  bg: string;
  title: string;
  sub: string;
}

function timeAgo(iso?: string): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const RecentActivity: React.FC = () => {
  const { data: leavesData, isLoading: loadingLeaves } = useGetMyLeavesQuery();
  const { data: timesheetsData, isLoading: loadingTimesheets } =
    useGetMyTimesheetsQuery();
  const { data: regData, isLoading: loadingReg } =
    useGetMyRegularizationRequestsQuery();

  const loading = loadingLeaves || loadingTimesheets || loadingReg;

  const items = useMemo(() => {
    const list: ActivityItem[] = [];

    (leavesData?.data || []).forEach((leave) => {
      const status = (leave.status || "").toLowerCase();
      list.push({
        id: `leave-${leave.id}`,
        timestamp: leave.createdAt,
        Icon: getWorkflowStatusIcon(status || "leave"),
        tint: "#10B981",
        bg: "#ECFDF5",
        title:
          status === "approved"
            ? "Leave approved"
            : status === "pending"
              ? "Leave submitted"
              : "Leave updated",
        sub: `${leave.leaveTypeName || "Leave"} · ${leave.startDate} – ${leave.endDate}`,
      });
    });

    (timesheetsData?.data || []).forEach((ts) => {
      list.push({
        id: `ts-${ts.id}`,
        timestamp: ts.createdAt,
        Icon: getWorkflowStatusIcon("submitted"),
        tint: "#2563EB",
        bg: "#EFF6FF",
        title: "Timesheet submitted",
        sub: `${ts.date} · ${ts.totalHours}h logged`,
      });
    });

    (regData?.data || []).forEach((reg) => {
      const statusLabel = (reg.statusLabel || "").toLowerCase();
      list.push({
        id: `reg-${reg.id}`,
        timestamp: reg.createdAt || reg.appliedDate,
        Icon: getWorkflowStatusIcon("regularization"),
        tint: "#6D28D9",
        bg: "#F3EEFF",
        title: statusLabel
          ? `Regularization ${statusLabel}`
          : "Regularization submitted",
        sub: `${reg.date} · ${reg.regularizationTypeName || reg.regularizationType}`,
      });
    });

    return list
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      .slice(0, 6);
  }, [leavesData, timesheetsData, regData]);

  return (
    <div className={`${CARD_CLASS} p-4 sm:p-5 flex flex-col h-auto lg:h-[480px] min-h-[300px]`}>
      <h3 className="m-0 mb-4 text-base font-bold text-[#1A1730] flex items-center gap-2 shrink-0">
        <Activity className="w-[18px] h-[18px] text-[#6D28D9]" />
        Recent Activity
      </h3>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-[#E6E3F0] pr-1">
        {loading ? (
          <div className="flex flex-col gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-3.5">
                <ShimmerBlock className="w-[38px] h-[38px] rounded-[11px] flex-none" />
                <div className="flex-1 space-y-2">
                  <ShimmerBlock className="h-4 w-2/3" />
                  <ShimmerBlock className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-4">
            <div className="w-14 h-14 bg-layout-hover rounded-full flex items-center justify-center mb-3 text-text-tertiary">
              <Activity className="w-6 h-6" />
            </div>
            <p className="text-body-sm font-semibold text-text-secondary">No recent activity</p>
            <p className="text-caption text-text-tertiary mt-1">
              Your latest actions will appear here
            </p>
          </div>
        ) : (
          <div className="flex flex-col space-y-1">
            {items.map((item, index) => (
              <div key={item.id} className="flex gap-3.5">
                <div className="flex flex-col items-center flex-none">
                  <span
                    className="w-[38px] h-[38px] rounded-xl grid place-items-center"
                    style={{ background: item.bg }}
                  >
                    <item.Icon
                      className="w-[18px] h-[18px]"
                      style={{ color: item.tint }}
                    />
                  </span>
                  {index < items.length - 1 && (
                    <span
                      className="flex-1 w-[2px] min-h-[14px] my-1 bg-border-secondary"
                    />
                  )}
                </div>
                <div className="flex-1 pb-3 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-body-sm font-bold text-text-primary truncate">
                      {item.title}
                    </span>
                    <span className="text-caption text-text-tertiary whitespace-nowrap">
                      {timeAgo(item.timestamp)}
                    </span>
                  </div>
                  <div className="text-caption text-text-secondary mt-0.5 truncate">
                    {item.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivity;
